import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { isFileAccessRestrictedStatus } from '../storageAccess/fileAccessRestrictedUrls';

export type MediaVideoLoadState = 'idle' | 'loading' | 'loaded' | 'error';

export interface UseMediaVideoLoadStateResult {
  state: MediaVideoLoadState;
  errorMessage: string | null;
  onLoaded: () => void;
  onError: () => void;
  onVideoMount: (el: HTMLVideoElement | null) => void;
}

const LOAD_TIMEOUT_MS = 45_000;

async function diagnoseVideoSrc(
  videoSrc: string,
  onAccessRestricted?: (status: number) => void
): Promise<string> {
  try {
    const res = await apiFetch(videoSrc, { headers: { Range: 'bytes=0-1' } });
    if (isFileAccessRestrictedStatus(res.status)) {
      onAccessRestricted?.(res.status);
      return 'Access to this video is restricted.';
    }
    try {
      await res.body?.cancel();
    } catch {
      /* ignore */
    }
    if (!res.ok) {
      return res.status === 404
        ? 'Video file was not found.'
        : `Could not load video (HTTP ${res.status}).`;
    }
    return 'This browser could not decode the video. Try MP4 (H.264) or use Download.';
  } catch (error) {
    return error instanceof Error ? error.message : 'Could not load this video.';
  }
}

/**
 * Video load gate for preview popup / full-page viewer.
 * Clears the spinner on metadata/canplay, diagnoses failures via a late Range probe
 * (so we do not race a second download against the video element), and times out hung streams.
 */
export function useMediaVideoLoadState(
  videoSrc: string,
  options?: {
    onAccessRestricted?: (status: number) => void;
  }
): UseMediaVideoLoadStateResult {
  const [state, setState] = useState<MediaVideoLoadState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const onAccessRestricted = options?.onAccessRestricted;

  useEffect(() => {
    if (!videoSrc) {
      setState('idle');
      setErrorMessage(null);
      return;
    }

    let cancelled = false;
    setState('loading');
    setErrorMessage(null);

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        if (cancelled) return;
        const message = await diagnoseVideoSrc(videoSrc, onAccessRestricted);
        if (cancelled) return;
        setState((current) => {
          if (current !== 'loading') return current;
          setErrorMessage(
            message.startsWith('This browser could not decode')
              ? 'Video is taking too long to load. Try Open viewer or Download.'
              : message
          );
          return 'error';
        });
      })();
    }, LOAD_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [videoSrc, onAccessRestricted]);

  const onLoaded = useCallback(() => {
    setState('loaded');
    setErrorMessage(null);
  }, []);

  const onError = useCallback(() => {
    void (async () => {
      if (!videoSrc) {
        setErrorMessage('Could not load this video.');
        setState('error');
        return;
      }
      const message = await diagnoseVideoSrc(videoSrc, onAccessRestricted);
      setErrorMessage(message);
      setState('error');
    })();
  }, [videoSrc, onAccessRestricted]);

  const onVideoMount = useCallback(
    (el: HTMLVideoElement | null) => {
      if (el && el.readyState >= HTMLMediaElement.HAVE_METADATA) {
        onLoaded();
      }
    },
    [onLoaded]
  );

  return { state, errorMessage, onLoaded, onError, onVideoMount };
}
